import React from 'react';
import { PageHeader, PageFooter } from './PageDecoration';

export const ExecutiveSummaryPage = ({ name, pageNum = 3 }) => {
    return (
        <div className="pdf-page executive-summary" style={{ height: '297mm', width: '210mm', position: 'relative', background: '#fff', padding: '120px 80px 80px' }}>
            <PageHeader sectionNumber={0} />
            <div style={{ maxWidth: '600px' }}>
                <h1 style={{ fontSize: '32px', fontWeight: '800', color: '#1e293b', marginBottom: '30px' }}>EXECUTIVE SUMMARY</h1>
                <p style={{ fontSize: '15px', fontWeight: '600', color: '#059669', marginBottom: '20px' }}>Dear {name},</p>
                <p style={{ fontSize: '14px', color: '#4b5563', lineHeight: '1.8', marginBottom: '20px' }}>
                    True financial freedom is not about how much money you earn. It is about how much of that money
                    stays with you and works for you. This report is a mirror of your current financial reality.
                </p>
                <p style={{ fontSize: '14px', color: '#4b5563', lineHeight: '1.8', marginBottom: '20px' }}>
                    Over the next 18 pages, we will diagnose your wealth machine, point out the hidden leakages,
                    and provide a surgical roadmap to help you achieve your goal of ₹10 Crores.
                </p>
                <p style={{ fontSize: '14px', color: '#4b5563', lineHeight: '1.8', marginBottom: '40px' }}>
                    Please read this with an open mind. Some numbers might be uncomfortable, but they are the
                    first step toward your transformation.
                </p>
                <div style={{ marginTop: '60px' }}>
                    <p style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b' }}>Yathartha Financial Team</p>
                    <p style={{ fontSize: '12px', color: '#64748b' }}>Precision Wealth Diagnosis</p>
                </div>
            </div>
            <PageFooter pageNumber={pageNum} clientName={name} />
        </div>
    );
};

export const SnapshotPage = ({ calculations, name, pageNum = 4 }) => {
    const m = calculations.metrics || {};
    return (
        <div className="pdf-page snapshot-page" style={{ height: '297mm', width: '210mm', position: 'relative', background: '#fff', padding: '120px 80px 80px' }}>
            <PageHeader sectionNumber={1} />
            <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', marginBottom: '10px' }}>01 YOUR FINANCIAL SNAPSHOT AT A GLANCE</h2>
            <p style={{ fontSize: '13px', color: '#4b5563', lineHeight: '1.6', marginBottom: '40px' }}>
                {name}, here is your entire financial life laid out on a single page.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginBottom: '40px' }}>
                <div style={{ background: '#f8fafc', padding: '25px', borderRadius: '24px', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>Monthly Net Income</div>
                    <div style={{ fontSize: '24px', fontWeight: '800', color: '#059669' }}>₹{(m.totalMonthlyIncome || 0).toLocaleString()}</div>
                </div>
                <div style={{ background: '#f8fafc', padding: '25px', borderRadius: '24px', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>Current Monthly Savings</div>
                    <div style={{ fontSize: '24px', fontWeight: '800', color: '#059669' }}>₹{(m.totalMonthlyInvestments || 0).toLocaleString()}</div>
                </div>
            </div>

            {/* Simple Snapshot table */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', overflow: 'hidden' }}>
                <div style={{ display: 'flex', background: '#1e293b', color: '#fff', padding: '12px 20px', fontSize: '12px', fontWeight: '600' }}>
                    <div style={{ width: '40%' }}>Category</div>
                    <div style={{ width: '30%' }}>Monthly Amount</div>
                    <div style={{ width: '30%' }}>Status</div>
                </div>
                {[
                    { label: 'Earning Power', val: m.totalMonthlyIncome, status: 'Healthy' },
                    { label: 'Living Expenses', val: m.totalExpenses, status: 'Necessary' },
                    { label: 'EMI Burdens', val: m.totalEMI, status: m.dtiRatio > 35 ? 'Critical' : 'Healthy' },
                    { label: 'Investments', val: m.totalMonthlyInvestments, status: m.savingsRate < 20 ? 'Needs Attention' : 'Healthy' }
                ].map((item, idx) => (
                    <div key={idx} style={{ display: 'flex', padding: '15px 20px', borderBottom: '1px solid #f1f5f9', fontSize: '14px' }}>
                        <div style={{ width: '40%', fontWeight: '600' }}>{item.label}</div>
                        <div style={{ width: '30%' }}>₹{Math.round(item.val || 0).toLocaleString()}</div>
                        <div style={{ width: '30%', color: item.status.includes('Healthy') ? '#059669' : '#dc2626', fontWeight: '700' }}>{item.status}</div>
                    </div>
                ))}
            </div>

            <PageFooter pageNumber={pageNum} clientName={name} />
        </div>
    );
};

export const AttentionPage = ({ calculations, name, pageNum = 7 }) => {
    const m = calculations.metrics || {};
    const fmt = (v) => v ? Math.round(v).toLocaleString('en-IN') : '0';

    return (
        <div className="pdf-page attention-page" style={{ height: '297mm', width: '210mm', position: 'relative', background: '#fff', padding: '100px 60px 60px', color: '#1e293b', overflow: 'hidden', border: '1px solid #eee' }}>
            <PageHeader sectionNumber={1} />

            <div style={{ marginBottom: '30px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#1a2744', padding: '4px 12px', borderRadius: '4px', width: 'fit-content', marginBottom: '10px' }}>
                    <span style={{ color: 'white', fontSize: '10px', fontWeight: '800', letterSpacing: '1px' }}>SECTION 01</span>
                </div>
                <h1 style={{ fontSize: '28px', fontWeight: '900', color: '#059669', lineHeight: '1.2', margin: 0 }}>
                    The 5 Gaps <span style={{ color: '#64748b', fontWeight: '400' }}>That Need Your</span><br />
                    Attention Now
                </h1>
            </div>

            <div style={{ position: 'relative', paddingLeft: '40px' }}>
                {/* Timeline Line */}
                <div style={{ position: 'absolute', left: '19px', top: '0', bottom: '0', width: '1px', background: '#e2e8f0' }}></div>

                {[
                    {
                        label: "Life Insurance Gap!", val: `Rs. ${fmt(calculations.lifeInsurance?.totalCover / 100000 || 10)}L vs. Rs. 1Cr+ needed`, desc: "Family is dangerously exposed.", sev: "Critical", color: "#c0392b", dark: "#8e1d1d", icon: (
                            <img src="/icons/icon-insurance.png" alt="Insurance" style={{ width: '32px', height: '32px', filter: 'brightness(0) invert(1)' }} />
                        )
                    },
                    {
                        label: "Emergency Fund!", val: `Rs. ${fmt(m.totalLiquid)} vs. Rs. ${fmt((m.totalMonthlyExpenses || m.totalExpenses || (m.totalMonthlyIncome * 0.4) || 30000) * 6)} Target (${fmt(Math.max(0, (m.totalMonthlyExpenses || (m.totalMonthlyIncome * 0.4) || 30000) * 6 - m.totalLiquid))} Needed)`, desc: "One event can derail plan.", sev: "URGENT", color: "#e67e22", dark: "#d35400", icon: (
                            <img src="/icons/icon-emergency.png" alt="Emergency" style={{ width: '32px', height: '32px', filter: 'brightness(0) invert(1)' }} />
                        )
                    },
                    {
                        label: "Savings Rate:", val: `${m.savingsRate?.toFixed(1)}% vs. 20% target`, desc: "Costs Rs. 2.8Cr over 25 years.", sev: "HIGH", color: "#2980b9", dark: "#21618c", icon: (
                            <img src="/icons/icon-savings.png" alt="Savings" style={{ width: '32px', height: '32px', filter: 'brightness(0) invert(1)' }} />
                        )
                    }
                ].map((gap, idx) => (
                    <div key={idx} style={{ position: 'relative', marginBottom: '25px', display: 'flex', alignItems: 'center', gap: '30px' }}>
                        {/* Card */}
                        <div style={{ position: 'relative', flexGrow: 1 }}>
                            {/* Pill */}
                            <div style={{ position: 'absolute', top: '-8px', left: '100px', transform: 'translateX(-50%)', background: gap.dark, color: '#fff', fontSize: '9px', fontWeight: '800', padding: '3px 15px', borderRadius: '20px', zIndex: 10, whiteSpace: 'nowrap' }}>
                                {gap.label}
                            </div>
                            <div style={{ background: gap.color, borderRadius: '8px', display: 'flex', overflow: 'hidden', height: '65px' }}>
                                <div style={{ width: '80px', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{gap.icon}</div>
                                <div style={{ padding: '10px 20px', color: '#fff' }}>
                                    <div style={{ fontSize: '15px', fontWeight: '800' }}>{gap.val}</div>
                                    <div style={{ fontSize: '10px', opacity: 0.9 }}>{gap.desc}</div>
                                </div>
                            </div>
                            {/* Triangle Pointing to Timeline */}
                            <div style={{ position: 'absolute', right: '-8px', top: '50%', transform: 'translateY(-50%)', width: '0', height: '0', borderTop: '8px solid transparent', borderBottom: '8px solid transparent', borderLeft: `8px solid ${gap.color}` }}></div>
                        </div>

                        {/* Timeline Node */}
                        <div style={{ width: '12px', height: '12px', background: '#fff', border: '3px solid #cbd5e1', borderRadius: '50%', position: 'absolute', left: '-27px', top: '50%', transform: 'translateY(-50%)' }}></div>

                        {/* Severity */}
                        <div style={{ width: '120px', border: `1px solid ${gap.color}`, borderRadius: '20px', padding: '4px 10px', textAlign: 'center', color: gap.color, fontWeight: '800', fontSize: '11px', position: 'relative' }}>
                            {gap.sev}
                            <div style={{ position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)', width: '15px', height: '5px', border: `1px solid ${gap.color}`, opacity: 0.5 }}></div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Big Picture */}
            <div style={{ marginTop: '30px', border: '1.5px solid #059669', borderRadius: '16px', padding: '20px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '900', color: '#059669', margin: '0 0 5px 0' }}>The Big Picture <span style={{ color: '#94a3b8', fontWeight: '400' }}>— Where You Are Headed</span></h3>
                <p style={{ fontSize: '11px', color: '#475569', lineHeight: '1.5', margin: 0 }}>
                    If {name.split(' ')[0]} takes the actions in this report, her financial score moves from {calculations.grades?.overall} to 80+ in two years.
                    Her retirement corpus at age 60 is ₹4.34 Crore — exceeding her target by ₹84 Lakhs.
                </p>
            </div>

            <PageFooter pageNumber={pageNum} clientName={name} />
        </div>
    );
};

export const IssuePageTemplate = ({ idx, rec, name, pageNum = 8 + idx }) => {
    return (
        <div className="pdf-page issue-page" style={{ height: '297mm', width: '210mm', position: 'relative', background: '#fff', padding: '120px 80px 80px' }}>
            <PageHeader sectionNumber={4} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '40px' }}>
                <div>
                    <div style={{ fontSize: '12px', fontWeight: '800', color: '#dc2626', marginBottom: '5px' }}>GAP ANALYSIS 0{idx + 1}</div>
                    <h1 style={{ fontSize: '32px', fontWeight: '800', color: '#111827' }}>{rec.category}</h1>
                </div>
                <div style={{ background: '#fecaca', color: '#dc2626', padding: '8px 16px', borderRadius: '8px', fontSize: '12px', fontWeight: '800' }}>
                    URGENT ATTENTION
                </div>
            </div>

            <div style={{ background: '#fff', border: '1px solid #fee2e2', borderRadius: '32px', overflow: 'hidden', marginBottom: '30px' }}>
                <div style={{ background: '#fef2f2', padding: '30px', borderBottom: '1px solid #fee2e2' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#991b1b', marginBottom: '10px' }}>THE PROBLEM</h3>
                    <p style={{ fontSize: '14px', color: '#b91c1c', lineHeight: '1.6' }}>{rec.details}</p>
                </div>
                <div style={{ padding: '30px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#166534', marginBottom: '10px' }}>THE SOLUTION</h3>
                    <p style={{ fontSize: '14px', color: '#15803d', lineHeight: '1.6' }}>{rec.action}</p>
                </div>
            </div>

            <div style={{ background: '#f8fafc', padding: '30px', borderRadius: '24px', border: '1px solid #e2e8f0' }}>
                <h4 style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b', marginBottom: '10px' }}>WHY IT MATTERS?</h4>
                <p style={{ fontSize: '12px', color: '#64748b', lineHeight: '1.6' }}>
                    If left unaddressed, this issue will cause a drag of approximately 15-20% on your net worth
                    growth over the next 10 years. Fixing this now is equivalent to getting an immediate pay raise.
                </p>
            </div>
            <PageFooter pageNumber={pageNum} clientName={name} />
        </div>
    );
};


export const RoadmapPageTemplate = ({ calculations, name, data = {}, pageNum = 8 }) => {
    const personalInfo = data?.personalInfo || {};
    const isBiz = data?.incomeType === 'business' || calculations?.incomeType === 'business';
    const sInc = (isBiz ? data?.businessIncome : data?.salaryIncome) || {};
    const sInv = (isBiz ? data?.businessInvestment : data?.salaryInvestment) || {};
    const section3 = data?.section3 || {};
    const section4 = data?.section4 || {};
    const m = calculations?.metrics || {};

    const isCouple = personalInfo.familyType === 'couple';
    const jointSalary = data?.jointSalary || {};
    const sectionA = jointSalary.sectionA || {};
    const sectionB = jointSalary.sectionB || {};
    const sectionC = jointSalary.sectionC || {};
    const sectionD = jointSalary.sectionD || {};
    const sectionE = jointSalary.sectionE || {};
    const sectionF = jointSalary.sectionF || {};
    const sectionG = jointSalary.sectionG || {};

    const safeParse = (v) => {
        if (typeof v === 'number') return v;
        if (!v) return 0;
        return parseFloat(v.toString().replace(/[₹, \s]/g, '')) || 0;
    };

    const fmt = v => Math.round(v || 0).toLocaleString('en-IN');
    const formatCover = (val) => {
        if (val >= 10000000) return `${(val / 10000000).toFixed(1).replace(/\.0$/, '')}Cr`;
        return `${Math.round(val / 100000)}L`;
    };
    const formatLakhs = (val) => {
        if (val >= 10000000) return `${(val / 10000000).toFixed(2)}Cr`;
        return `${Math.round(val / 100000)}L`;
    };

    // First names
    const userName = name || "Client";
    const userFirstName = userName.split(' ')[0];
    const hName = sectionA?.husbandName || "Husband";
    const wName = sectionA?.wifeName || "Wife";
    const hFirstName = hName.split(' ')[0];
    const wFirstName = wName.split(' ')[0];

    // Income
    const monthlyIncome = m.totalMonthlyIncome || safeParse(personalInfo.monthlySalary) || 0;
    const annualIncome = monthlyIncome * 12;

    // 1. Term Insurance Gap
    let row1Action = "";
    if (isCouple) {
        const hSalary = safeParse(sectionB.husbandSalary) || 0;
        const wSalary = safeParse(sectionB.wifeSalary) || 0;
        const hTargetCover = hSalary * 12 * 10;
        const wTargetCover = wSalary * 12 * 10;
        const hCurrentCover = safeParse(sectionE.husbandLifeCover) || 0;
        const wCurrentCover = safeParse(sectionE.wifeLifeCover) || 0;
        const hGap = Math.max(0, hTargetCover - hCurrentCover);
        const wGap = Math.max(0, wTargetCover - wCurrentCover);

        if (hGap > 0 && wGap > 0) {
            row1Action = `${hFirstName} buys ₹${formatCover(hTargetCover)} term insurance, ${wFirstName} buys ₹${formatCover(wTargetCover)}`;
        } else if (hGap > 0) {
            row1Action = `${hFirstName} buys ₹${formatCover(hTargetCover)} term insurance`;
        } else if (wGap > 0) {
            row1Action = `${wFirstName} buys ₹${formatCover(wTargetCover)} term insurance`;
        } else {
            row1Action = "Term insurance is already optimized for both";
        }
    } else {
        const uTargetCover = annualIncome * 10;
        const uCurrentCover = safeParse(data.salaryIncome?.lifeInsuranceCover) || safeParse(data.section4?.lifeInsuranceCover) || 0;
        const uGap = Math.max(0, uTargetCover - uCurrentCover);
        if (uGap > 0) {
            row1Action = `${userFirstName} buys ₹${formatCover(uTargetCover)} term insurance`;
        } else {
            row1Action = "Term insurance is already optimized";
        }
    }

    // 2. Emergency Fund Transfer
    const fallbackExp = monthlyIncome * 0.4;
    const targetEF = m.emergencyFundTarget || m.targetEmergency || Math.round((m.totalEssentialExpenses || fallbackExp || 30000) * 6);
    const currentEF = isCouple
        ? (safeParse(sectionG.emergencyFund) || 0)
        : (safeParse(data.salaryInvestment?.emergencyFund) || safeParse(data.salaryInvestment?.liquidFund) || 0);
    const efGap = Math.max(0, targetEF - currentEF);
    const efMonthlyTransfer = efGap > 0 ? Math.max(1000, Math.round(efGap / 18 / 1000) * 1000) : 0;

    let row2Action = "";
    if (isCouple) {
        row2Action = efMonthlyTransfer > 0
            ? `Open joint liquid fund, start ₹${fmt(efMonthlyTransfer)}/month emergency transfer`
            : "Emergency fund is already fully funded";
    } else {
        row2Action = efMonthlyTransfer > 0
            ? `Open liquid fund, start ₹${fmt(efMonthlyTransfer)}/month emergency transfer`
            : "Emergency fund is already fully funded";
    }

    // 3. Tax Savings Regime
    const taxSaving = m.tax?.taxSavings || calculations?.taxSavings || 0;
    const recommendedRegime = (m.tax?.recommendedRegime === 'New' || calculations?.tax?.recommendedRegime === 'New') ? 'New' : 'Old';
    const row3Action = isCouple
        ? `Declare recommended Tax Regimes to HR`
        : `Declare recommended ${recommendedRegime} Tax Regime to HR`;
    const row3Impact = taxSaving > 0
        ? `Save ₹${fmt(taxSaving)}/year`
        : "Regime optimized";

    // 4. PPF Targets
    let row4Action = "";
    if (isCouple) {
        const hPPF = safeParse(sectionD.husbandPPF) || 0;
        const wPPF = safeParse(sectionD.wifePPF) || 0;
        const hPPF_target = hPPF > 0 ? hPPF : 3500;
        const wPPF_target = wPPF > 0 ? wPPF : 3500;
        row4Action = `Increase joint PPF to ₹${fmt(hPPF_target + wPPF_target)}/month (₹${fmt(hPPF_target)} for ${hFirstName}, ₹${fmt(wPPF_target)} for ${wFirstName})`;
    } else {
        const uPPF = safeParse(data.salaryInvestment?.ppf) || safeParse(data.salaryIncome?.ppf) || 0;
        const uPPF_target = uPPF > 0 ? uPPF : 5000;
        row4Action = `Increase PPF to ₹${fmt(uPPF_target)}/month`;
    }

    // 5. Personal Loan prepayments
    const personalLoanEMI = isCouple
        ? (safeParse(sectionF.personalLoanEMI) || safeParse(sectionC.personalLoanEMI) || 0)
        : (safeParse(data.salaryIncome?.personalLoanEMI) || m.personalLoanEMI || 0);
    const personalLoanBalance = isCouple
        ? (safeParse(sectionF.personalLoanOutstanding) || 0)
        : (safeParse(data.salaryIncome?.personalLoanOutstanding) || m.personalLoanBalance || 0);
    const extraPayment = Math.round(personalLoanEMI * 0.5);
    const monthsToClose = personalLoanEMI > 0 ? Math.ceil(personalLoanBalance / (personalLoanEMI + extraPayment)) : 0;
    const interestSaved = Math.round(personalLoanBalance * 0.14 * (monthsToClose / 24)); // approx interest saved

    let row5Action = "";
    let row5Impact = "";
    if (personalLoanEMI > 0) {
        row5Action = `Pay ₹${fmt(extraPayment)} extra on personal loan monthly`;
        row5Impact = `Close in ${monthsToClose} months, save ₹${fmt(interestSaved)}`;
    } else {
        row5Action = "Direct untracked surplus into systematic goal-based mutual fund SIPs";
        row5Impact = "Accelerate your ₹10Cr wealth compound trajectory";
    }

    // 6. ELSS SIP
    let row6Action = "";
    if (isCouple) {
        row6Action = "Start joint ELSS SIP of ₹6k/month (₹3k each)";
    } else {
        row6Action = "Start ELSS SIP of ₹5k/month";
    }

    // 7. NPS
    let row7Action = "";
    let row7Impact = "";
    if (isCouple) {
        row7Action = "Start joint NPS: ₹4k/month (₹2k each)";
        row7Impact = "Extra ₹50k deduction each";
    } else {
        row7Action = "Start NPS: ₹4.2k/month to utilise Section 80CCD(1B)";
        row7Impact = "Extra ₹50k tax deduction";
    }

    // 8. Redirect savings
    let row8Action = "";
    let row8Impact = "";
    const redirectAmount = personalLoanEMI + extraPayment;
    const largeCap = Math.round(redirectAmount * 0.4 / 1000) * 1000;
    const midCap = Math.round(redirectAmount * 0.3 / 1000) * 1000;
    const debt = redirectAmount - largeCap - midCap;

    if (personalLoanEMI > 0) {
        row8Action = isCouple
            ? `Personal loan closed. Redirect ₹${fmt(redirectAmount)}/month to joint SIPs`
            : `Personal loan closed. Redirect ₹${fmt(redirectAmount)}/month to SIPs`;
        row8Impact = `₹${fmt(largeCap)} to large cap, ₹${fmt(midCap)} to mid-cap, ₹${fmt(debt)} to debt`;
    } else {
        const fallbackSurplus = Math.round(monthlyIncome * 0.3 / 1000) * 1000;
        const surplusAmt = m.monthlySurplus || fallbackSurplus;
        row8Action = isCouple
            ? `Redirect savings surplus of ₹${fmt(surplusAmt)}/month to joint SIPs`
            : `Redirect savings surplus of ₹${fmt(surplusAmt)}/month to SIPs`;
        row8Impact = `₹${fmt(Math.round(surplusAmt * 0.4))} to large cap, ₹${fmt(Math.round(surplusAmt * 0.3))} to mid-cap, ₹${fmt(Math.round(surplusAmt * 0.3))} to debt`;
    }

    // 9. Emergency fund fully funded
    const row9Action = `Emergency fund fully funded at ₹${formatLakhs(targetEF)} (Month 12)`;
    const row9Impact = efMonthlyTransfer > 0
        ? `Full safety. Redirect ₹${fmt(efMonthlyTransfer)}/month to SIPs`
        : "Full safety cushion achieved";

    // 10. Target SIP
    const targetSIP = Math.round(monthlyIncome * 0.4 / 5000) * 5000;
    const retirementTarget = annualIncome * 15;
    const retirementCorpus = Math.round(retirementTarget * 1.2);
    const retirementCorpusCr = (retirementCorpus / 10000000).toFixed(2);

    const row10Action = isCouple
        ? `Target joint SIP: ₹${fmt(targetSIP)}/month by Year 2`
        : `Target individual SIP: ₹${fmt(targetSIP)}/month by Year 2`;
    const row10Impact = `₹${retirementCorpusCr}Cr retirement corpus at 60`;

    // Table CSS Styling
    const tableHeaderStyle = {
        background: '#069668', // Yathartha Brand Green (#069668)
        color: '#ffffff', // Clean white contrast text
        borderBottom: '2px solid #047857',
        padding: '12px 14px',
        fontWeight: '800', // Bold header
        fontSize: '11.5px',
        textTransform: 'uppercase',
        letterSpacing: '0.8px',
        fontFamily: 'Montserrat, sans-serif',
        textAlign: 'left'
    };

    const tableCellStyle = {
        borderBottom: '1px solid #e6fbf2', // Clean horizontal borders
        padding: '12px 14px',
        color: '#1f2937', // Slate-800 high contrast dark gray
        fontWeight: '500', // Medium font weight for high readability
        fontSize: '12.5px',
        lineHeight: '1.45',
        fontFamily: 'Montserrat, sans-serif',
        textAlign: 'left'
    };

    return (
        <div className="pdf-page roadmap-page" style={{ height: '297mm', width: '210mm', position: 'relative', background: '#fff', padding: '60px 50px' }}>
            <PageHeader sectionNumber={5} />

            <div style={{ borderBottom: '2px solid #069668', paddingBottom: '10px', marginBottom: '22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h1 style={{ fontSize: '20px', fontWeight: '900', color: '#065f46', margin: 0, fontFamily: 'Montserrat, sans-serif', letterSpacing: '0.5px' }}>
                    {isCouple ? "JOINT PERSONAL FINANCIAL ROADMAP" : "PERSONAL FINANCIAL ROADMAP"}
                </h1>
                <span style={{ background: '#ecfdf5', color: '#069668', fontSize: '9px', fontWeight: '800', padding: '3px 8px', borderRadius: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Section 05
                </span>
            </div>

            {/* Phase 1 Card Container */}
            <div style={{
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '16px',
                padding: '20px 24px',
                marginBottom: '24px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.02)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                    <span style={{
                        background: '#ecfdf5',
                        color: '#065f46',
                        fontSize: '9.5px',
                        fontWeight: '800',
                        padding: '4px 12px',
                        borderRadius: '20px',
                        border: '1px solid #a7f3d0',
                        letterSpacing: '0.8px',
                        textTransform: 'uppercase'
                    }}>
                        Phase 1
                    </span>
                    <span style={{ fontSize: '14px', fontWeight: '800', color: '#1e293b', fontFamily: 'Montserrat, sans-serif' }}>
                        This Month – Foundation Sprint
                    </span>
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={{ ...tableHeaderStyle, width: '6%', textAlign: 'center' }}></th>
                            <th style={{ ...tableHeaderStyle, width: '47%' }}>Action</th>
                            <th style={{ ...tableHeaderStyle, width: '18%' }}>By When</th>
                            <th style={{ ...tableHeaderStyle, width: '29%' }}>{isCouple ? "Joint Impact" : "Impact"}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {[
                            ["1", row1Action, "This week", "Critical vulnerability closed"],
                            ["2", row2Action, "This week", "Safety net begins"],
                            ["3", row3Action, "This week", row3Impact],
                            ["4", row4Action, "This month", isCouple ? "Max 80C for both" : "Max 80C limit"]
                        ].map((row, idx) => (
                            <tr key={idx} style={{ background: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                                <td style={{ ...tableCellStyle, width: '6%', textAlign: 'center', verticalAlign: 'middle' }}>
                                    <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '22px',
                                        height: '22px',
                                        borderRadius: '50%',
                                        background: '#069668',
                                        color: '#ffffff',
                                        fontSize: '11px',
                                        fontWeight: '800'
                                    }}>
                                        {row[0]}
                                    </span>
                                </td>
                                <td style={tableCellStyle}>{row[1]}</td>
                                <td style={tableCellStyle}>{row[2]}</td>
                                <td style={tableCellStyle}>{row[3]}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Phase 2 Card Container */}
            <div style={{
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '16px',
                padding: '20px 24px',
                marginBottom: '24px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.02)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                    <span style={{
                        background: '#ecfdf5',
                        color: '#065f46',
                        fontSize: '9.5px',
                        fontWeight: '800',
                        padding: '4px 12px',
                        borderRadius: '20px',
                        border: '1px solid #a7f3d0',
                        letterSpacing: '0.8px',
                        textTransform: 'uppercase'
                    }}>
                        Phase 2
                    </span>
                    <span style={{ fontSize: '14px', fontWeight: '800', color: '#1e293b', fontFamily: 'Montserrat, sans-serif' }}>
                        Months 2-6 – Debt Fire & Momentum
                    </span>
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={{ ...tableHeaderStyle, width: '6%', textAlign: 'center' }}></th>
                            <th style={{ ...tableHeaderStyle, width: '58%' }}>Action</th>
                            <th style={{ ...tableHeaderStyle, width: '36%' }}>{isCouple ? "Joint Impact" : "Impact"}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {[
                            ["5", row5Action, row5Impact],
                            ["6", row6Action, "Tax saving + equity growth"],
                            ["7", row7Action, row7Impact]
                        ].map((row, idx) => (
                            <tr key={idx} style={{ background: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                                <td style={{ ...tableCellStyle, width: '6%', textAlign: 'center', verticalAlign: 'middle' }}>
                                    <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '22px',
                                        height: '22px',
                                        borderRadius: '50%',
                                        background: '#069668',
                                        color: '#ffffff',
                                        fontSize: '11px',
                                        fontWeight: '800'
                                    }}>
                                        {row[0]}
                                    </span>
                                </td>
                                <td style={tableCellStyle}>{row[1]}</td>
                                <td style={tableCellStyle}>{row[2]}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Phase 3 Card Container */}
            <div style={{
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '16px',
                padding: '20px 24px',
                marginBottom: '24px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.02)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                    <span style={{
                        background: '#ecfdf5',
                        color: '#065f46',
                        fontSize: '9.5px',
                        fontWeight: '800',
                        padding: '4px 12px',
                        borderRadius: '20px',
                        border: '1px solid #a7f3d0',
                        letterSpacing: '0.8px',
                        textTransform: 'uppercase'
                    }}>
                        Phase 3
                    </span>
                    <span style={{ fontSize: '14px', fontWeight: '800', color: '#1e293b', fontFamily: 'Montserrat, sans-serif' }}>
                        Month 12 & Beyond – Wealth Acceleration
                    </span>
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={{ ...tableHeaderStyle, width: '6%', textAlign: 'center' }}></th>
                            <th style={{ ...tableHeaderStyle, width: '58%' }}>Action</th>
                            <th style={{ ...tableHeaderStyle, width: '36%' }}>{isCouple ? "Joint Impact" : "Impact"}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {[
                            ["8", row8Action, row8Impact],
                            ["9", row9Action, row9Impact],
                            ["10", row10Action, row10Impact]
                        ].map((row, idx) => (
                            <tr key={idx} style={{ background: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                                <td style={{ ...tableCellStyle, width: '6%', textAlign: 'center', verticalAlign: 'middle' }}>
                                    <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '22px',
                                        height: '22px',
                                        borderRadius: '50%',
                                        background: '#069668',
                                        color: '#ffffff',
                                        fontSize: '11px',
                                        fontWeight: '800'
                                    }}>
                                        {row[0]}
                                    </span>
                                </td>
                                <td style={tableCellStyle}>{row[1]}</td>
                                <td style={tableCellStyle}>{row[2]}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <PageFooter pageNumber={pageNum} clientName={name} />
        </div>
    );
};
